import React, { useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Camp } from '../../utils/types';
import CampCard from './CampCard';
import CampDetails from './CampDetails';

import './CampTable.css';
import { useSearchParams } from 'react-router-dom';
import { ArrowUpDown } from 'lucide-react';
import NoCampsFound from './NoCampsFound';
import { useAnalytics } from '../../context/AnalyticsContext';
import { useCampContext } from '../../context/CampContext';
import { useFeatureFlag } from '../../context/FeatureFlagContext';
import { FeatureFlags } from '../../utils/featureFlags';
import { SortParams } from '../../api/camps';


type SortOption = 'default' | 'date-asc' | 'date-desc' | 'price-asc' | 'price-desc' | 'name-asc' | 'name-desc';

interface CampTableProps {
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMorePages?: boolean;
  isLoadingMore?: boolean;
  totalCamps?: number;
  onMouseEnter?: (organizerId?: number) => void;
  onMouseLeave?: () => void;
  onFiltersChange?: (filters: any) => void;
  onClearAll?: () => void;
  onSortChange?: (sort: SortParams) => void;
}

const CampTable: React.FC<CampTableProps> = ({
  isLoading = true,
  onLoadMore,
  hasMorePages = false,
  isLoadingMore = false,
  totalCamps = 0,
  onMouseEnter,
  onMouseLeave,
  onFiltersChange,
  onClearAll,
  onSortChange
}) => {
  const [selectedDates, setSelectedDates] = useState<Record<string, string>>({});
  const [showCampDetails, setShowCampDetails] = useState(false);
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackEvent } = useAnalytics();
  const modalRef = useRef<HTMLDivElement>(null);
  const { filters, camps, visibleCamps, schedules } = useCampContext();
  const newsletterSignupEnabled = useFeatureFlag(FeatureFlags.BOOK_NOW_NEWSLETTER_SIGNUP, false);
  const [sortBy, setSortBy] = useState<SortOption>('default');

  const favoriteId = '1';

  const removeFilter = (filterType: string) => {
    if (!onFiltersChange) return;
    
    const newFilters = { ...filters };
    
    switch (filterType) {
      case 'activityKeywords':
        delete newFilters.activityKeywords;
        break;
      case 'dateRange':
        delete newFilters.startDate;
        delete newFilters.endDate;
        break;
      case 'ages':
        delete newFilters.ages;
        break;
      case 'duration':
        delete newFilters.duration;
        break;
      case 'priceRange':
        delete newFilters.minPrice;
        delete newFilters.maxPrice;
        break;
      case 'minRating':
        delete newFilters.minRating;
        break;
    }
    
    trackEvent('results_page_filter_removed', { filter_type: filterType });
    onFiltersChange(newFilters);
  };

  const handleDateChange = (campId: string, dateId: string) => {
    setSelectedDates(prev => ({
      ...prev,
      [campId]: dateId
    }));
  };

  const updateURLWithSelectedCamp = (campId: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('selected_camp', campId);
      return newParams;
    });
  };

  const removeSelectedCampFromURL = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('selected_camp');
      return newParams;
    });
  };

  const handleSimilarCampClick = (similarCamp: Camp) => {
    setSelectedCamp(similarCamp);
    updateURLWithSelectedCamp(similarCamp.id);
    // Scroll to top of modal when content changes
    setTimeout(() => {
      if (modalRef.current) {
        modalRef.current.scrollTop = 0;
      }
    }, 100);
  };

  useEffect(() => {
    const selectedCampId = searchParams.get('selected_camp');
    if (selectedCampId) {
      const selectedCamp = camps?.find(camp => String(camp.id) === selectedCampId);
      if (selectedCamp) {
        setSelectedCamp(selectedCamp)
        setShowCampDetails(true)
      }
    }
  }, [searchParams, camps]);

  // Build map of scheduled date IDs for each camp from ALL schedules
  const scheduledDateIds = useMemo(() => {
    const map = new Map<string, string[]>();

    if (schedules && schedules.length > 0) {
      for (const schedule of schedules) {
        if (schedule?.scheduled_camps) {
          for (const scheduled of schedule.scheduled_camps) {
            const campId = String(scheduled.camp.id); // Normalize to string
            const dateId = String(scheduled.camp_date.id); // Normalize to string

            if (!map.has(campId)) {
              map.set(campId, []);
            }
            // Avoid duplicate date IDs for the same camp
            if (!map.get(campId)!.includes(dateId)) {
              map.get(campId)!.push(dateId);
            }
          }
        }
      }
    }
    return map;
  }, [schedules]);

  // Use visibleCamps if available (map-filtered), otherwise use all camps
  const campsToDisplay = visibleCamps !== null ? visibleCamps : camps;
  const paginatedCamps = campsToDisplay ?? [];
  const sentinelRef = useRef<HTMLDivElement>(null);
  const prevCampsLengthRef = useRef(0);

  // Trigger server-side fetchNextPage when sentinel becomes visible
  useEffect(() => {
    if (!sentinelRef.current || isLoading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMorePages && onLoadMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      { rootMargin: '100px', threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMorePages, onLoadMore, isLoadingMore, isLoading]);

  // Scroll to top only when dataset is replaced (filter/bounds change), not when it grows (new page)
  useEffect(() => {
    const newLength = paginatedCamps.length;
    if (prevCampsLengthRef.current > 0 && newLength <= prevCampsLengthRef.current) {
      window.scrollTo({ top: 0, behavior: 'instant' as any });
    }
    prevCampsLengthRef.current = newLength;
  }, [paginatedCamps]);

  if (isLoading || campsToDisplay === null) {
    return (
      <div className="table-section">
        <div className="table-header">
          <h4>Loading Camps...</h4>
        </div>
        <div className="camp-cards-container">
          <div className="camp-cards-loading">
            <p>Loading camps...</p>
          </div>
        </div>
      </div>
    );
  }  

  if (campsToDisplay.length === 0) {
    return (
      <NoCampsFound
        onRemoveFilter={removeFilter}
        onClearAll={onClearAll}
      />
    );
  }

  return (
    <div className="table-section">
      <div className="camp-sorter-header">
        <h4>{totalCamps} Camps found in the map area</h4>
        <div className="sort-container">
          <ArrowUpDown className="icon icon--sm" />
          <select
            value={sortBy}
            onChange={(e) => {
              const value = e.target.value as SortOption;
              setSortBy(value);
              if (onSortChange) {
                if (value === 'default') {
                  onSortChange({});
                } else {
                  const [sort_by, sort_order] = value.split('-');
                  onSortChange({ sort_by, sort_order });
                }
              }
            }}
            className="sort-select"
          >
            <option value="default">Recommended</option>
            <option value="date-asc">Date (Earliest First)</option>
            <option value="date-desc">Date (Latest First)</option>
            <option value="price-asc">Price per day (Low to High)</option>
            <option value="price-desc">Price per day (High to Low)</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
          </select>
        </div>
      </div>
      <div className="camp-cards-container">
        <div className="camp-cards-grid">
          {paginatedCamps.map((camp) => (
            <CampCard
              key={camp.id}
              camp={camp}
              selectedDate={selectedDates[camp.id]}
              onDateChange={handleDateChange}
              favoriteId={favoriteId}
              scheduledDateIds={scheduledDateIds.get(String(camp.id))}
              newsletterSignupEnabled={newsletterSignupEnabled}
              onCampClick={(camp: Camp) => {
                trackEvent('results_page_camp_table_camp_clicked', { camp_id: camp.id });
                setSelectedCamp(camp);
                setShowCampDetails(true);
                updateURLWithSelectedCamp(camp.id);
              }}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            />
          ))}
        </div>

        {hasMorePages && !isLoading && (
          <div ref={sentinelRef} className="infinite-scroll-sentinel">
            <div className="loading-more-indicator">
              <p>Loading more camps...</p>
            </div>
          </div>
        )}

        {showCampDetails && selectedCamp && createPortal(
          <CampDetails
            ref={modalRef}
            camp={selectedCamp}
            isOpen={showCampDetails}
            onClose={() => {
              setShowCampDetails(false);
              setSelectedCamp(null);
              removeSelectedCampFromURL();
            }}
            selectedDate={selectedDates[selectedCamp.id]}
            onDateChange={handleDateChange}
            favoriteId={favoriteId}
            newsletterSignupEnabled={newsletterSignupEnabled}
            onSimilarCampClick={handleSimilarCampClick}
          />,
          document.body
        )}
      </div>
    </div>
  );
};

export default CampTable;
